import { useEffect, useRef, useState } from "react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { getPaymentsConfig, createPayPalOrder, capturePayPalOrder } from "@/lib/api";

const errMsg = (err, fallback) => {
  const d = err?.response?.data?.detail;
  return typeof d === "string" ? d : fallback;
};

export const PayPalCheckout = ({ validate, buildPayload, onPaid, amount }) => {
  const [clientId, setClientId] = useState(undefined);
  const [busy, setBusy] = useState(false);
  const orderRef = useRef(null);
  const createFailed = useRef(false);
  const [ready, setReady] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!clientId || ready || attempt >= 3) return;
    const t = setTimeout(() => setAttempt((a) => a + 1), 3500);
    return () => clearTimeout(t);
  }, [clientId, ready, attempt]);
  const latest = useRef({ validate, buildPayload, onPaid });
  latest.current = { validate, buildPayload, onPaid };

  useEffect(() => { getPaymentsConfig().then((c) => setClientId(c.client_id || null)).catch(() => setClientId(null)); }, []);

  if (clientId === undefined) return <div className="grid h-24 place-items-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>;
  if (!clientId) return <p data-testid="paypal-unavailable" className="rounded-lg bg-slate-100 p-4 text-center text-sm text-slate-600">Online payment is being set up. Please message us on Telegram to order.</p>;

  return (
    <div data-testid="paypal-checkout" className="relative min-h-[100px]">
      {!ready && attempt < 3 && <div data-testid="paypal-loading" className="pointer-events-none absolute inset-0 z-10 flex min-h-[100px] items-center justify-center gap-2 rounded-lg bg-white text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading payment options…</div>}
      {busy && <div className="absolute inset-0 z-10 grid place-items-center rounded-lg bg-white/80"><Loader2 className="h-6 w-6 animate-spin text-blue-600" /></div>}
      <PayPalScriptProvider options={{ clientId, currency: "GBP", intent: "capture", components: "buttons", "enable-funding": "card" }}>
        <PayPalButtons
          key={attempt}
          onInit={() => setReady(true)}
          style={{ layout: "vertical", shape: "pill", label: "pay", height: 48 }}
          onClick={(_, actions) => (latest.current.validate() ? actions.resolve() : actions.reject())}
          createOrder={async () => {
            createFailed.current = false;
            try {
              const res = await createPayPalOrder(latest.current.buildPayload());
              orderRef.current = res.order_id;
              return res.paypal_order_id;
            } catch (err) {
              createFailed.current = true;
              toast.error(errMsg(err, "Could not start payment. Please try again."));
              throw err;
            }
          }}
          onApprove={async (data) => {
            setBusy(true);
            try {
              const res = await capturePayPalOrder(data.orderID);
              latest.current.onPaid(res.order_id);
            } catch (err) {
              toast.error(errMsg(err, "Payment could not be completed. You have not been charged."));
              setBusy(false);
            }
          }}
          onCancel={() => toast("Payment cancelled — your cart is still saved.")}
          onError={() => { if (!createFailed.current) toast.error("PayPal ran into a problem. Please try again."); }}
        />
      </PayPalScriptProvider>
      {!ready && attempt >= 3 && (
        <button type="button" data-testid="paypal-reload" onClick={() => window.location.reload()} className="w-full rounded-lg bg-slate-100 p-3 text-sm text-slate-700 hover:bg-slate-200">Payment options didn't load — tap to reload</button>
      )}
    </div>
  );
};

export default PayPalCheckout;
